import { useEffect, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { RefreshCw, Shield, Trash2, UserPlus, Wallet, X } from 'lucide-react'
import type { User, XaiBalance } from '@shared/types'
import { useStore } from '../../store'
import { api } from '../../lib/api'
import { fmtCompact } from '../../lib/format'
import { cn, relativeTime } from '../../lib/utils'

function formatMoney(n: number | null, ccy: string | null): string {
  if (n == null) return ccy ? `— ${ccy}` : '—'
  const amt = n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  if (ccy == null || ccy.toUpperCase() === 'USD') return `$${amt}`
  return `${amt} ${ccy}`
}

function BalanceBar() {
  const [bal, setBal] = useState<XaiBalance | null>(null)
  const [busy, setBusy] = useState(false)
  const load = (force = false) => {
    setBusy(true)
    api
      .xaiBalance(force)
      .then(setBal)
      .catch(() => setBal(null))
      .finally(() => setBusy(false))
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => load(), [])

  return (
    <div className="flex items-center justify-between gap-3 border-b border-[var(--line-soft)] bg-[var(--color-surface-2)] px-4 py-2.5">
      <div className="flex min-w-0 items-center gap-2">
        <Wallet size={15} className="shrink-0 text-fg-muted" />
        {bal == null ? (
          <span className="text-[12.5px] text-fg-subtle">{busy ? 'Checking xAI balance…' : 'xAI balance unavailable'}</span>
        ) : !bal.configured ? (
          <span className="text-[12px] leading-snug text-fg-subtle">
            Set <code className="rounded bg-[var(--color-inset)] px-1 text-fg-muted">XAI_MANAGEMENT_API_KEY</code> in your{' '}
            <code className="rounded bg-[var(--color-inset)] px-1 text-fg-muted">.env</code> to show the team’s xAI prepaid balance — a <b>team Management key</b> from console.x.ai (team owner only; not the chat key).
          </span>
        ) : bal.ok ? (
          <span className="truncate text-[13px] text-fg" title={typeof bal.raw === 'object' ? JSON.stringify(bal.raw) : String(bal.raw ?? '')}>
            {bal.owed != null ? (
              <>
                {/* The real bill: usage − prepaid (+VAT), auto-charged at month end. */}
                <span title="Postpaid amount owed this cycle — auto-charged at month end">
                  xAI owed: <span className="num font-semibold">{formatMoney(bal.owed, bal.currency)}</span>
                </span>
                {bal.spentThisCycle != null && (
                  <span className="ml-2 text-[11.5px] text-fg-subtle">
                    · <span className="num">{formatMoney(bal.spentThisCycle, bal.currency)}</span> used this cycle
                    {bal.prepaidApplied ? (
                      <>
                        {' '}(<span className="num">{formatMoney(bal.prepaidApplied, bal.currency)}</span> prepaid)
                      </>
                    ) : null}
                  </span>
                )}
                <span className="ml-2 text-[11.5px] text-fg-subtle" title="Remaining prepaid credit (consumed before postpaid)">
                  · prepaid left <span className="num">{formatMoney(bal.balance, bal.currency)}</span>
                </span>
              </>
            ) : (
              <>
                xAI balance: <span className="num font-semibold">{formatMoney(bal.balance, bal.currency)}</span>
                {bal.spentThisCycle != null && (
                  <span className="ml-2 text-[11.5px] text-fg-subtle">
                    · <span className="num">{formatMoney(bal.spentThisCycle, bal.currency)}</span> spent this cycle
                  </span>
                )}
                {bal.balance == null && <span className="ml-1 text-[11px] text-fg-subtle">(see raw — hover)</span>}
              </>
            )}
          </span>
        ) : (
          <span className="truncate text-[12px] text-down" title={bal.error}>
            xAI balance error: {bal.error}
          </span>
        )}
      </div>
      <button
        onClick={() => load(true)}
        disabled={busy}
        className="shrink-0 rounded-md p-1 text-fg-subtle transition-colors hover:text-fg disabled:opacity-40"
        title="Refresh balance"
      >
        <RefreshCw size={13} className={busy ? 'animate-spin' : ''} />
      </button>
    </div>
  )
}

function LimitCell({ user }: { user: User }) {
  const patch = useStore((s) => s.adminPatchUser)
  const [val, setVal] = useState(user.tokenLimit != null ? String(user.tokenLimit) : '')
  const commit = () => {
    const t = val.trim()
    const n = t === '' ? null : Math.max(0, Math.floor(Number(t) || 0))
    void patch(user.id, { tokenLimit: n })
  }
  const over = user.tokenLimit != null && user.tokensIn + user.tokensOut >= user.tokenLimit
  return (
    <input
      value={val}
      onChange={(e) => setVal(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
      placeholder="∞"
      title="Total token limit (blank = unlimited)"
      className={cn(
        'num w-20 rounded border bg-[var(--color-inset)] px-1.5 py-0.5 text-right text-[12px] focus:outline-none',
        over ? 'border-down text-down' : 'border-[var(--line-soft)] text-fg',
      )}
    />
  )
}

// Per-user task quotas: daily / occasional. Blank = role default (admins unlimited → "∞").
function TaskLimitCell({ user }: { user: User }) {
  const patch = useStore((s) => s.adminPatchUser)
  const [d, setD] = useState(user.taskDailyLimit != null ? String(user.taskDailyLimit) : '')
  const [o, setO] = useState(user.taskOccasionalLimit != null ? String(user.taskOccasionalLimit) : '')
  const commit = () => {
    const num = (v: string) => (v.trim() === '' ? null : Math.max(0, Math.floor(Number(v) || 0)))
    void patch(user.id, { taskDailyLimit: num(d), taskOccasionalLimit: num(o) })
  }
  const ph = user.role === 'admin' ? '∞' : ''
  const box =
    'num w-9 rounded border border-[var(--line-soft)] bg-[var(--color-inset)] px-1 py-0.5 text-right text-[12px] text-fg focus:outline-none'
  return (
    <div className="flex items-center justify-end gap-0.5">
      <input value={d} onChange={(e) => setD(e.target.value)} onBlur={commit} onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()} placeholder={ph || '2'} title="Daily task limit (blank = default)" className={box} />
      <span className="text-fg-faint">/</span>
      <input value={o} onChange={(e) => setO(e.target.value)} onBlur={commit} onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()} placeholder={ph || '10'} title="Occasional task limit (blank = default)" className={box} />
    </div>
  )
}

// Per-user max files attachable to one message (blank = default 10).
function MaxFilesCell({ user }: { user: User }) {
  const patch = useStore((s) => s.adminPatchUser)
  const [val, setVal] = useState(user.maxFiles != null ? String(user.maxFiles) : '')
  const commit = () => {
    const t = val.trim()
    const n = t === '' ? null : Math.min(50, Math.max(1, Math.floor(Number(t) || 0)))
    void patch(user.id, { maxFiles: n })
  }
  return (
    <input
      value={val}
      onChange={(e) => setVal(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
      placeholder="10"
      title="Max files per message (blank = default 10, max 50)"
      className="num w-12 rounded border border-[var(--line-soft)] bg-[var(--color-inset)] px-1.5 py-0.5 text-right text-[12px] text-fg focus:outline-none"
    />
  )
}

export function AdminPanel() {
  const open = useStore((s) => s.adminOpen)
  const toggle = useStore((s) => s.toggleAdmin)
  const users = useStore((s) => s.users)
  const me = useStore((s) => s.user)
  const createUser = useStore((s) => s.adminCreateUser)
  const deleteUser = useStore((s) => s.adminDeleteUser)

  const [u, setU] = useState('')
  const [p, setP] = useState('')
  const [name, setName] = useState('')
  const [role, setRole] = useState('user')
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const add = async () => {
    setErr(null)
    setBusy(true)
    try {
      await createUser({ username: u.trim(), password: p, name: name.trim() || undefined, role })
      setU('')
      setP('')
      setName('')
      setRole('user')
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not create user')
    } finally {
      setBusy(false)
    }
  }

  const inputCls =
    'min-w-0 flex-1 rounded-lg border border-[var(--line-soft)] bg-[var(--color-inset)] px-2.5 py-2 text-[12.5px] text-fg placeholder:text-fg-faint focus:outline-none'

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="absolute inset-0 bg-black/55" onClick={() => toggle(false)} />
          <motion.div
            initial={{ y: 16, scale: 0.98, opacity: 0 }}
            animate={{ y: 0, scale: 1, opacity: 1 }}
            exit={{ y: 16, scale: 0.98, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 320, damping: 30 }}
            className="glass relative flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden"
            style={{ borderRadius: 'var(--radius-lg)' }}
          >
            <header className="flex items-center justify-between border-b border-[var(--line-soft)] px-4 py-3">
              <div className="flex items-center gap-2">
                <Shield size={16} className="text-fg-muted" />
                <span className="text-[14px] font-semibold text-fg">Users &amp; usage</span>
              </div>
              <button
                onClick={() => toggle(false)}
                className="rounded-md p-1 text-fg-subtle hover:text-fg"
                title="Close"
              >
                <X size={16} />
              </button>
            </header>

            <BalanceBar />

            <div className="border-b border-[var(--line-soft)] p-3">
              <div className="mb-2 text-[11px] uppercase tracking-[0.1em] text-fg-faint">
                Add user — only you can register accounts
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                <input placeholder="username" value={u} onChange={(e) => setU(e.target.value)} className={inputCls} />
                <input placeholder="password (min 8)" type="password" value={p} onChange={(e) => setP(e.target.value)} className={inputCls} />
                <input placeholder="name (optional)" value={name} onChange={(e) => setName(e.target.value)} className={inputCls} />
                <select
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  className="rounded-lg border border-[var(--line-soft)] bg-[var(--color-inset)] px-2 py-2 text-[12.5px] text-fg focus:outline-none"
                >
                  <option value="user">user</option>
                  <option value="admin">admin</option>
                </select>
                <button
                  onClick={add}
                  disabled={busy || !u.trim() || p.length < 8}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--color-accent)] px-3 py-2 text-[12px] font-semibold text-[var(--color-base)] transition-opacity disabled:opacity-40"
                >
                  <UserPlus size={14} /> Add
                </button>
              </div>
              {err && <div className="mt-1.5 text-[12px] text-down">{err}</div>}
            </div>

            <div className="flex-1 overflow-y-auto p-3">
              <table className="w-full text-[12.5px]">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wide text-fg-faint">
                    <th className="pb-2 text-left">User</th>
                    <th className="pb-2 text-center">Role</th>
                    <th className="pb-2 text-right">Input tok</th>
                    <th className="pb-2 text-right">Output tok</th>
                    <th className="pb-2 text-right">Limit</th>
                    <th className="pb-2 text-right" title="Task quota: daily / occasional (blank = default)">Tasks d/o</th>
                    <th className="pb-2 text-right" title="Max files attachable per message (blank = default 10)">Files</th>
                    <th className="pb-2 text-right">Last seen</th>
                    <th className="pb-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((usr) => (
                    <tr key={usr.id} className="border-t border-[var(--separator)]">
                      <td className="py-2">
                        <div className="font-medium text-fg">{usr.username}</div>
                        {usr.name && <div className="text-[11px] text-fg-subtle">{usr.name}</div>}
                      </td>
                      <td className="text-center">
                        <span
                          className={cn(
                            'rounded px-1.5 py-0.5 text-[10px] uppercase tracking-wide',
                            usr.role === 'admin' ? 'bg-[var(--accent-wash)] text-fg' : 'text-fg-subtle',
                          )}
                        >
                          {usr.role}
                        </span>
                      </td>
                      <td className="num text-right text-fg">{fmtCompact(usr.tokensIn)}</td>
                      <td className="num text-right text-fg">{fmtCompact(usr.tokensOut)}</td>
                      <td className="text-right">
                        <LimitCell user={usr} />
                      </td>
                      <td className="text-right">
                        <TaskLimitCell user={usr} />
                      </td>
                      <td className="text-right">
                        <MaxFilesCell user={usr} />
                      </td>
                      <td className="num text-right text-fg-subtle">
                        {usr.lastLogin ? relativeTime(usr.lastLogin) : '—'}
                      </td>
                      <td className="text-right">
                        {usr.id !== me?.id && (
                          <button
                            onClick={() => {
                              if (confirm(`Delete ${usr.username}? Their chats, projects and memory are removed.`))
                                void deleteUser(usr.id)
                            }}
                            className="rounded p-1 text-fg-subtle hover:text-down"
                            title="Delete user"
                          >
                            <Trash2 size={13} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
